import React, { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { permissions } from "../../permissions";
import { Input } from "@headlessui/react";

// Icons
import plusIcon from "@surgia/ui/icons/actions/plus.svg";
import minusIcon from "@surgia/ui/icons/actions/minus.svg";
import deleteIcon from "@surgia/ui/icons/actions/delete.svg";

interface QuantityControlsProps {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  minQuantity: number;
  showDeleteButton?: boolean;
  maxQuantity: number;
  disabled?: boolean;
  setQuantity: (quantity: number) => void;
  item?: any;
  inputValue: string;
  setInputValue: (value: string) => void;
  resetOnInvalidInput?: boolean;
}

const QuantityControls: React.FC<QuantityControlsProps> = ({
  quantity,
  onRemove,
  showDeleteButton = true,
  disabled = false,
  setQuantity,
  minQuantity,
  maxQuantity,
  inputValue,
  setInputValue,
  resetOnInvalidInput = false,
}) => {
  // Get user permissions from global state
  const userPermissions = useSelector(
    (state: RootState) =>
      state.auth.globalState.user_permissions?.map(
        (permission) => permission.name
      ) || []
  );

  // Check if user has edit permission
  const hasEditPermission = userPermissions.includes(
    permissions.EDIT_DRAFT_PURCHASE_REQUEST
  );

  // Combined disabled state
  const isDisabled = disabled || !hasEditPermission;

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  // Update local input value when quantity prop changes
  useEffect(() => {
    setInputValue(quantity?.toString());
  }, [quantity, setInputValue]);

  const handleDecrease = () => {
    if (isDisabled) return;

    const newQuantity = quantity - 1;

    if (newQuantity < minQuantity) {
      toast.error(`Quantity cannot be less than ${minQuantity}`);
      return;
    }

    setQuantity(newQuantity);
    setInputValue(newQuantity?.toString());
  };

  const handleIncrease = () => {
    if (isDisabled) return;

    const newQuantity = quantity + 1;

    if (newQuantity > maxQuantity) {
      toast.error(`Quantity cannot be more than ${maxQuantity}`);
      return;
    }

    setQuantity(newQuantity);
    setInputValue(newQuantity?.toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    const value = e.target.value;

    // Only allow numbers and empty string
    const numericValue = value.replace(/[^0-9]/g, "");

    setInputValue(numericValue); // Always update the input value for typing

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to validate after user stops typing
    timeoutRef.current = setTimeout(() => {
      const numValue = parseInt(numericValue);
      if (!isNaN(numValue) && numValue >= 0) {
        // Check range before setting quantity
        if (numValue < minQuantity) {
          toast.error(`Quantity cannot be less than ${minQuantity}`);
          if (resetOnInvalidInput) {
            setInputValue(minQuantity?.toString());
          }
          return;
        }
        if (numValue > maxQuantity) {
          toast.error(`Quantity cannot be more than ${maxQuantity}`);
          if (resetOnInvalidInput) {
            setInputValue(maxQuantity?.toString());
          }
          return;
        }

        setQuantity(numValue);
      }
    }, 500); // Wait 500ms after user stops typing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="flex items-center gap-1">
      {showDeleteButton && (
        <button
          onClick={onRemove}
          className={`p-1 rounded transition-colors ${
            isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
          disabled={isDisabled}
        >
          <img src={deleteIcon} alt="Remove" />
        </button>
      )}

      <button
        onClick={handleDecrease}
        className={`p-1 rounded transition-colors ${
          isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
        disabled={isDisabled}
      >
        <img src={minusIcon} alt="Decrease" />
      </button>

      <Input
        id="quantity-input"
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        className={`w-16 !text-center h-[40px] border border-[#D5D7DA] 
        rounded-md text-[16px] text-[#181D27] focus:outline-none focus:ring-2  !focus:border-transparent ${
          isDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        disabled={isDisabled}
      />

      <button
        onClick={handleIncrease}
        className={`p-1 rounded transition-colors ${
          isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
        disabled={isDisabled}
      >
        <img src={plusIcon} alt="Increase" />
      </button>
    </div>
  );
};

export default QuantityControls;
